import { z } from "zod";

const nameSchema = (label: string) =>
  z
    .string()
    .trim()
    .min(1, `${label} är obligatoriskt`)
    .max(100, `${label} får vara högst 100 tecken`);

const idSchema = z.string().uuid().nullable();
const dateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Välj ett giltigt datum");
const thresholdSchema = z
  .number({ error: "Ange ett heltal" })
  .int("Ange ett heltal")
  .positive("Gränsen måste vara större än 0");

export const patchBossSchema = z.object({
  id: idSchema,
  name: nameSchema("Bossnamn"),
});

export const patchRaidSchema = z.object({
  id: idSchema,
  name: nameSchema("Raid"),
  bosses: z.array(patchBossSchema).min(1, "Minst en boss krävs"),
});

const patchDraftSchema = z
  .object({
    name: nameSchema("Patch"),
    startsOn: dateSchema,
    endsOn: dateSchema,
    vaultThreshold1: thresholdSchema,
    vaultThreshold2: thresholdSchema,
    vaultThreshold3: thresholdSchema,
    raids: z.array(patchRaidSchema).min(1, "Minst en raid krävs"),
  })
  .superRefine((patch, context) => {
    if (patch.endsOn < patch.startsOn) {
      context.addIssue({
        code: "custom",
        path: ["endsOn"],
        message: "Slut måste vara samma som eller efter start",
      });
    }

    if (
      patch.vaultThreshold1 >= patch.vaultThreshold2 ||
      patch.vaultThreshold2 >= patch.vaultThreshold3
    ) {
      context.addIssue({
        code: "custom",
        path: ["vaultThreshold3"],
        message: "Vault-gränserna måste vara strikt stigande",
      });
    }

    const totalBosses = patch.raids.reduce(
      (total, raid) => total + raid.bosses.length,
      0,
    );
    if (patch.vaultThreshold3 > totalBosses) {
      context.addIssue({
        code: "custom",
        path: ["vaultThreshold3"],
        message: "Högsta gränsen får inte överstiga antalet bossar",
      });
    }

    const raidNames = new Set<string>();
    patch.raids.forEach((raid, raidIndex) => {
      const normalizedRaidName = raid.name.toLocaleLowerCase("sv");
      if (raidNames.has(normalizedRaidName)) {
        context.addIssue({
          code: "custom",
          path: ["raids", raidIndex, "name"],
          message: "Raider måste ha unika namn inom patchen",
        });
      }
      raidNames.add(normalizedRaidName);

      const bossNames = new Set<string>();
      raid.bosses.forEach((boss, bossIndex) => {
        const normalizedBossName = boss.name.toLocaleLowerCase("sv");
        if (bossNames.has(normalizedBossName)) {
          context.addIssue({
            code: "custom",
            path: ["raids", raidIndex, "bosses", bossIndex, "name"],
            message: "Bossnamn måste vara unika inom raiden",
          });
        }
        bossNames.add(normalizedBossName);
      });
    });
  });

// Array order is the form's source of truth. Add contiguous positions only after
// validation so every create/update sends a fully normalized tree.
export const patchFormSchema = patchDraftSchema.transform((patch) => ({
  ...patch,
  raids: patch.raids.map((raid, sortPosition) => ({
    ...raid,
    sortPosition,
    bosses: raid.bosses.map((boss, bossSortPosition) => ({
      ...boss,
      sortPosition: bossSortPosition,
    })),
  })),
}));

export type PatchFormInput = z.input<typeof patchFormSchema>;
export type PatchFormValues = z.output<typeof patchFormSchema>;
